import { I18nPluralPipe, KeyValuePipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
    type AfterViewChecked,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    DOCUMENT,
    Injector,
    inject,
    input,
    signal,
    type WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { type Button, ButtonModule } from '@openng/optimus-ui/button';
import { ButtonGroupModule } from '@openng/optimus-ui/buttongroup';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { DrawerModule } from '@openng/optimus-ui/drawer';
import { InplaceModule } from '@openng/optimus-ui/inplace';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { ListboxModule } from '@openng/optimus-ui/listbox';
import { MessageModule } from '@openng/optimus-ui/message';
import { PanelModule } from '@openng/optimus-ui/panel';
import { PopoverModule } from '@openng/optimus-ui/popover';
import { ProgressBarModule } from '@openng/optimus-ui/progressbar';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';
import { SelectModule } from '@openng/optimus-ui/select';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { TableModule } from '@openng/optimus-ui/table';
import { TabsModule } from '@openng/optimus-ui/tabs';
import { TagModule } from '@openng/optimus-ui/tag';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { TimelineModule } from '@openng/optimus-ui/timeline';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { ArrayStore } from 'devextreme/common/data';
import { type DxDataGridComponent, DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { groupBy, isEqual, isNil, keyBy, mapValues, sortBy, sumBy, uniqWith } from 'lodash-es';
import moment from 'moment';
import { injectParams } from 'ngxtension/inject-params';
import { linkedQueryParam, paramToNumber } from 'ngxtension/linked-query-param';
import {
    auditTime,
    catchError,
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    filter,
    forkJoin,
    from,
    map,
    mergeMap,
    of,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { InputEditorComponent } from '../input-editor/input-editor.component';
import { NotFoundComponent } from '../not-found/not-found.component';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { MarkdownLazyModule } from '@app/markdown-lazy.module';
import type { GlobalFunctionService } from '@app/modules/assets/services/global-function.service';
import { PromptService } from '@app/modules/assets/services/prompt.service';
import type { ReportsService } from '@app/modules/assets/services/reports.service';
import { AnnotationQueueService } from '@app/modules/monitor/services/annotation-queue.service';
import { SimulationService } from '@app/modules/testing/services/simulation.service';
import { ChatComponent } from '@app/shared/components/chat/chat.component';
import type { InteractionAdapter } from '@app/shared/components/chat/model/chat.model';
import { CodeBlockComponent } from '@app/shared/components/code-block/code-block.component';
import { DefinitionComponent } from '@app/shared/components/definition/definition.component';
import { DiffViewerComponent } from '@app/shared/components/diff-viewer/diff-viewer.component';
import { ErrorBlockComponent } from '@app/shared/components/error-block/error-block.component';
import { FieldErrorComponent } from '@app/shared/components/field-error/field-error.component';
import { ForbiddenComponent } from '@app/shared/components/forbidden/forbidden.component';
import { InlineReportComponent } from '@app/shared/components/inline-report/inline-report.component';
import { InputContainerComponent } from '@app/shared/components/input-container/input-container.component';
import { InputSelectComponent } from '@app/shared/components/input-select/input-select.component';
import { InvalidProjectComponent } from '@app/shared/components/invalid-project/invalid-project.component';
import { JobFigureComponent } from '@app/shared/components/job-figure/job-figure.component';
import { JobWorkflowBuilderWrapperComponent } from '@app/shared/components/job-workflow/components/workflow-wrapper/job-workflow-wrapper.component';
import type { JobWorkflowStepLogTarget } from '@app/shared/components/job-workflow/types/job-workflow.types';
import { LoadingStateComponent } from '@app/shared/components/loading-state/loading-state.component';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import type { Item } from '@app/shared/components/registry/action-btns/action-btns.component';
import { JobModelUsageComponent } from '@app/shared/components/run-job/job-model-usage/job-model-usage.component';
import { JobResultsComponent } from '@app/shared/components/run-job/run-job-details/job-results/job-results.component';
import { RunJobDetailsComponent } from '@app/shared/components/run-job/run-job-details/run-job-details.component';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { SkeletonLoaderComponent } from '@app/shared/components/skeleton/skeleton-loader.component';
import { StatusIconComponent } from '@app/shared/components/status-icon/status-icon.component';
import {
    ACTIONS,
    EXECUTION_TYPE,
    GGX_GLOBAL_OBJECTS,
    INPUT_CASES,
    JOB_STEP_STATUS,
    MODEL_INPUT_TYPE,
    MOMENT_UTC_DATETIME_FORMAT,
    OBJECT_DISPLAY_NAMES,
    OBJECT_INFO,
    OBJECT_MODULE_MAP,
    OBJECTS,
    SCHEDULED_JOB_STATUS,
    SIMULATABLE_OBJECTS,
    SIMULTATION_TIME,
    SKELETON_TYPES,
} from '@app/shared/constants/shared.constant';
import type { DxDataGrid, DxDataGridColumn } from '@app/shared/interface/devextreme.interface';
import type { InputResponseModel } from '@app/shared/models/input-response.model';
import {
    CONVERSATION_COLUMN,
    type JobAdapter,
    type JobAsset,
    JobModel,
    type JobStep,
    type SimulationFigure,
} from '@app/shared/models/job.model';
import { CellPrettifyPipe } from '@app/shared/pipes/cell-prettify.pipe';
import { LocalDatePipe } from '@app/shared/pipes/date-format.pipe';
import { IncludesPipe } from '@app/shared/pipes/includes.pipe';
import { ObjectIconPipe } from '@app/shared/pipes/object-color.pipe';
import { TimeTakenPipe } from '@app/shared/pipes/time-taken.pipe';
import { TruncatePipe } from '@app/shared/pipes/truncate.pipe';
import { InputService } from '@app/shared/services/input.service';
import type { WithCrudService } from '@app/shared/services/mixins/crud.service';
import type { WithExportableService } from '@app/shared/services/mixins/export.service';
import type { WithJobService } from '@app/shared/services/mixins/job.service';
import type { WithWorkflowableService } from '@app/shared/services/mixins/workflow.service';
import { PermissionService } from '@app/shared/services/permission.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { WindowService } from '@app/shared/services/window.service';
import { DataGridHelpers } from '@app/shared/utils/data-grid-helpers';
import { scrollToBottom } from '@app/shared/utils/dom-helper';
import { Helpers, parseChatMessage } from '@app/shared/utils/helpers';
import { OBJECT_SERVICE_MAPPER } from '@app/shared/utils/object-service-mapper';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';
import { RouterHelper } from '@app/shared/utils/router-helper';

export interface ReportTab {
    title: string;
    reportId: number;
    evaluationId?: number;
    data: {
        reportOutputId?: number;
        width: string;
        leftSimFigure: SimulationFigure;
        rightSimFigure?: SimulationFigure;
        comparisonFigure?: SimulationFigure;
        rerunError?: string | null;
    }[];
    isDetailsVisible?: WritableSignal<boolean>;
    isAssetsVisible?: WritableSignal<boolean>;
    isLogsVisible?: WritableSignal<boolean>;
    previewData?: {
        isVisible: WritableSignal<boolean>;
        isLoading: WritableSignal<boolean>;
        gridDatasource: WritableSignal<ArrayStore<Record<string, string | number>, any>>;
        columns: WritableSignal<DxDataGridColumn[]>;
        infoTextMarker: WritableSignal<string>;
    };
}

type JobAssetWithLink = JobAsset & {
    link: string;
    bgColor?: string;
};

interface JobAssetGroup {
    name: string;
    objectType: OBJECTS;
    url: string;
    lastModifiedDate: string;
    bgColor: string;
    isModified: boolean;
}

interface RunDurationBreakdown {
    totalDuration: number;
    lifecycle: {
        label: string;
        value?: string;
        author?: string;
        markerClass: string;
    }[];
    stages: {
        label: string;
        status: JOB_STEP_STATUS;
        durationLabel: string;
        shareLabel: string;
        shareValue: number;
    }[];
}

@Component({
    selector: 'cor-job-dashboard',
    imports: [
        RunJobDetailsComponent,
        InvalidProjectComponent,
        SkeletonComponent,
        JobModelUsageComponent,
        JobResultsComponent,
        NoDataComponent,
        TabsModule,
        TimelineModule,
        KeyValuePipe,
        IncludesPipe,
        TooltipModule,
        JobFigureComponent,
        LoadingStateComponent,
        SkeletonLoaderComponent,
        RouterModule,
        PopoverModule,
        ProgressBarModule,
        ListboxModule,
        SelectModule,
        FormsModule,
        ReactiveFormsModule,
        TimeTakenPipe,
        CodeBlockComponent,
        ProgressSpinnerModule,
        DxDataGridModule,
        DialogModule,
        InplaceModule,
        InputTextModule,
        MessageModule,
        InputEditorComponent,
        TableModule,
        PanelModule,
        TextareaModule,
        MarkdownLazyModule,
        ButtonModule,
        ButtonGroupModule,
        ChatComponent,
        DiffViewerComponent,
        DrawerModule,
        StatusIconComponent,
        InputContainerComponent,
        InputSelectComponent,
        InlineReportComponent,
        DefinitionComponent,
        FieldErrorComponent,
        DatePickerModule,
        JobWorkflowBuilderWrapperComponent,
        ErrorBlockComponent,
        I18nPluralPipe,
        NgClass,
        NgStyle,
        NgTemplateOutlet,
        CellPrettifyPipe,
        LocalDatePipe,
        ObjectIconPipe,
        ForbiddenComponent,
        TagModule,
        NotFoundComponent,
        SelectButtonModule,
    ],
    templateUrl: './job-dashboard.component.html',
    styleUrl: './job-dashboard.component.scss',
    host: {
        class: 'tw:flex tw:flex-1 tw:flex-col tw:min-h-0',
    },
})
export class JobDashboardComponent implements AfterViewChecked {
    // Object Details: The ID of the Object whose dashboard we are viewing (taken from route params/data)
    readonly id = input(undefined);
    protected readonly object = input<OBJECTS>(null);
    protected readonly currentProject = injectParams<string>('projectSlug');
    private readonly routeObjectId = injectParams('id', { parse: paramToNumber() });
    private readonly routeSimulationId = injectParams('simulationId', { parse: paramToNumber() });
    readonly comparisonId = linkedQueryParam('comparisonId', {
        parse: paramToNumber(),
        stringify: value => value?.toString(),
    });
    readonly activeTabParam = linkedQueryParam('tab', {
        parse: paramToNumber(),
        stringify: value => value?.toString(),
    });
    readonly showLogsParam = linkedQueryParam('showLogs');

    protected service: WithCrudService<any> &
        WithJobService &
        Partial<WithExportableService> &
        WithWorkflowableService;

    protected readonly simulationService = inject(SimulationService);
    protected readonly annotationQueueService = inject(AnnotationQueueService);

    protected readonly item = signal<Item>(null);
    protected readonly notFound = signal<boolean>(false);
    protected readonly forbidden = signal(false);
    protected readonly isItemLoading = signal(false);
    protected readonly job = signal<JobModel>(null);
    protected readonly canCompare = computed(
        () => this.job()?.jobStatus === SCHEDULED_JOB_STATUS.COMPLETED,
    );
    protected readonly jobReports = computed(() =>
        (this.job()?.dataLogics || [])
            .filter(dataLogic => !!dataLogic.reportVersionId)
            .map(dataLogic => dataLogic.reportVersionId),
    );
    protected readonly isJobLoading = signal(false);
    protected readonly isJobPinLoading = signal(false);
    protected readonly isRefreshing = signal(false);
    protected readonly isRerunning = signal(false);
    protected readonly isRerunningAllFigures = signal(false);
    protected readonly activeIndex = signal(0); // Index of the tab that is currently visible
    protected readonly userHasWriteAccess = signal(false);
    protected readonly evalTabs = signal<ReportTab[]>([]);

    protected readonly addEvaluationDialog = {
        visible: signal(false),
        isSubmitting: signal(false),
        nameControl: new FormControl<string>('', Validators.required),
        descriptionControl: new FormControl<string>(''),
        dataLogicSourceControl: new FormControl<number>(null, Validators.required),
        dataLogics: computed(() =>
            (this.job()?.dataLogics || []).filter(dataLogic => isNil(dataLogic.dataLogicSourceId)),
        ),
        open: () => {
            this.addEvaluationDialog.nameControl.reset('');
            this.addEvaluationDialog.descriptionControl.reset('');
            this.addEvaluationDialog.dataLogicSourceControl.reset(null);
            this.addEvaluationDialog.visible.set(true);
        },
        close: () => {
            this.addEvaluationDialog.visible.set(false);
        },
    };

    protected readonly removeOldLabelDialog = {
        visible: signal(false),
        commentControl: new FormControl<string>(''),
        submit: () => {
            this.simulationService
                .removeOldLabel(this.job().id, {
                    comment: this.removeOldLabelDialog.commentControl.value,
                })
                .subscribe(({ result }) => {
                    this.job.set(JobModel.createFromAPI(result));
                    this.removeOldLabelDialog.visible.set(false);
                    this.toast.showSuccess('“Old” label removed');
                });
        },
    };

    // This is only used when we show the dashboard tab in the object page
    protected dashboardTab = {
        defaultDashboardJobId: signal<number>(null),
        jobList: signal<JobAdapter[]>([]),
        isJobListLoading: signal(false),
        selectedJobId: signal<number>(null),
        showChangeOption: signal(false),
        isSavingDashboard: signal(false),
        hasObjectWriteAccess: computed(() => this.item()?.actions?.[ACTIONS.WRITE]),
    };

    protected comparison = {
        isObjectSelectionVisible: signal(false),
        objectList: signal<{ id: number; name: string; version: number }[]>([]),
        isObjectListLoading: signal(false),
        selectedObjectId: signal<number>(null),
        jobList: signal<JobAdapter[]>([]),
        isJobListLoading: signal(false),
        selectedJobId: signal<number>(null),
        isComparisonViewActive: signal(false),
        leftJob: signal<JobModel>(null),
        rightJob: signal<JobModel>(null),
        isLoading: signal(false),
        flipped: signal(false), // to flip the left and right job
        // isBaseHillclimbJobDeleted will be false if the /compare API cannot return it's ID - this is typically
        // if the job has been deleted. In this case, we show
        isBaseHillclimbJobDeleted: signal(false),
    };

    // hillClimb is used only in comparison - when we want to show the diff of the prompt
    // FIXME: We should move this to comparison state
    protected hillClimb = {
        isLoading: signal(false),
        prompts: signal<
            {
                entityId: number;
                entityName: string;
                leftPromptTemplate?: string;
                rightPromptTemplate?: string;
            }[]
        >([]),
    };

    protected readonly reportEvaluations = {
        isLoading: signal(false),
        dataLogicInputList: signal<InputResponseModel[]>([]),
        dataPreparationStep: computed(() =>
            this.job()?.jobSteps.find(step =>
                step.title.startsWith(SIMULTATION_TIME.DATA_CREATION.valueOf()),
            ),
        ),
        executionStep: computed(() =>
            this.job()?.jobSteps.find(step =>
                step.title.startsWith(SIMULTATION_TIME.EXECUTION.valueOf()),
            ),
        ),
        inputResourcesList: computed((): InputResponseModel[] =>
            this.reportEvaluations
                .dataLogicInputList()
                .filter(
                    x => ![OBJECTS.ANNOTATION_QUEUE, OBJECTS.DATA_TABLE].includes(x.objectType),
                ),
        ),
        inputTablesList: computed((): InputResponseModel[] =>
            this.reportEvaluations
                .dataLogicInputList()
                .filter(x => x.objectType === OBJECTS.DATA_TABLE),
        ),
        inputAnnotationQueuesList: computed((): InputResponseModel[] =>
            this.reportEvaluations
                .dataLogicInputList()
                .filter(x => x.objectType === OBJECTS.ANNOTATION_QUEUE),
        ),
        evalMap: computed(
            // evalMap is the final thing which contains ass data taken from the JobModel in this eval
            (): Record<
                number,
                {
                    reportId: number;
                    dataLogic: ArrayItem<JobModel['dataLogics']>;
                    figures: SimulationFigure[];
                    jobAssets: JobAssetWithLink[];
                    jobAssetGroups: Record<string, JobAssetGroup[]>;
                    summary: JobStep & { totalOutputs: number };
                    durationBreakdown: RunDurationBreakdown;
                    logs: string;
                    dataPreparationLogs?: string;
                    owner?: string;
                }
            > =>
                keyBy(
                    this.evalTabs().map(tab => {
                        const dataLogic = this.job().dataLogics.find(
                            x => x.id === tab.evaluationId,
                        );
                        let evaluationStep: JobStep;

                        if (tab.reportId) {
                            evaluationStep = this.job().jobSteps.find(
                                step => !isNil(step.reportName) && step.reportName === tab.title,
                            );
                        } else {
                            // If reportId is not present, then use execution step
                            evaluationStep = this.job().jobSteps.find(step =>
                                step.title.startsWith(SIMULTATION_TIME.EXECUTION.valueOf()),
                            );
                        }
                        const logs = this.jobStepLogs().find(
                            logs => logs.stepId === evaluationStep?.id,
                        )?.logs;
                        const jobAssetsInfo = this.getObjectAssets(dataLogic?.jobAssets || []);

                        return {
                            reportId: tab.reportId,
                            evaluationId: tab.evaluationId,
                            dataLogic: {
                                ...dataLogic,
                                inputFeatureVersionIds: InputService.getUniqueIdForSelectedInputs(
                                    dataLogic,
                                    this.reportEvaluations.inputResourcesList(),
                                ),
                            },
                            figures: tab.data.map(figure => figure.leftSimFigure) || [],
                            jobAssets: jobAssetsInfo.jobAssets,
                            jobAssetGroups: jobAssetsInfo.jobAssetGroups,
                            summary: evaluationStep
                                ? {
                                      ...evaluationStep,
                                      totalOutputs: evaluationStep.passed + evaluationStep.failed,
                                  }
                                : undefined,
                            durationBreakdown: this.getRunDurationBreakdown(tab.title),
                            logs,
                            dataPreparationLogs: this.jobStepLogs().find(
                                logs =>
                                    logs.stepId ===
                                    this.reportEvaluations.dataPreparationStep()?.id,
                            )?.logs,
                            owner:
                                jobAssetsInfo.jobAssets.find(
                                    asset =>
                                        asset.objectType === OBJECTS.REPORT &&
                                        asset.entityId === tab.reportId,
                                )?.createdBy || this.item()?.createdBy,
                        };
                    }),
                    'evaluationId',
                ),
        ),
        dataPreview: {
            isVisible: signal(false),
            dataLogic: signal<ArrayItem<JobModel['dataLogics']>>(null),
            onShow: (dataLogic: ArrayItem<JobModel['dataLogics']>) => {
                this.reportEvaluations.dataPreview.isVisible.set(true);
                this.reportEvaluations.dataPreview.dataLogic.set(dataLogic);
            },
            onHide: () => {
                this.reportEvaluations.dataPreview.isVisible.set(false);
                this.reportEvaluations.dataPreview.dataLogic.set(null);
            },
        },
        stepLogsDialog: {
            isVisible: signal(false),
            title: signal(''),
            logs: signal(''),
            onShow: (target: JobWorkflowStepLogTarget) => {
                const step = this.reportEvaluations.stepLogsDialog.getStep(target);

                this.reportEvaluations.stepLogsDialog.title.set(step.label);
                this.reportEvaluations.stepLogsDialog.logs.set(
                    this.jobStepLogs().find(logs => logs.stepId === step?.id)?.logs ||
                        'No logs captured',
                );
                this.reportEvaluations.stepLogsDialog.isVisible.set(true);
            },
            getStep: (target: JobWorkflowStepLogTarget) => {
                if (target.stepType === 'dataPreparation') {
                    return this.job()?.jobSteps.find(step =>
                        step.title.startsWith(SIMULTATION_TIME.DATA_CREATION.valueOf()),
                    );
                }

                if (target.stepType === 'execution') {
                    return this.job()?.jobSteps.find(step =>
                        step.title.startsWith(SIMULTATION_TIME.EXECUTION.valueOf()),
                    );
                }

                if (target.stepType === 'report') {
                    return this.job()?.jobSteps.find(
                        step =>
                            step.title.startsWith(SIMULTATION_TIME.REPORT.valueOf()) &&
                            step.reportName === target.reportName,
                    );
                }
                return null;
            },
            onHide: () => {
                this.reportEvaluations.stepLogsDialog.isVisible.set(false);
                this.reportEvaluations.stepLogsDialog.title.set('');
                this.reportEvaluations.stepLogsDialog.logs.set('');
            },
        },
    };

    private readonly destroyRef = inject(DestroyRef);
    protected toast = inject(ToastNotificationService);
    private readonly route = inject(ActivatedRoute);
    private readonly injector = inject(Injector);
    private readonly cdr = inject(ChangeDetectorRef);
    protected confirmationService = inject(ConfirmationService);
    private readonly window = inject(WindowService);
    private readonly permissionService = inject(PermissionService);
    protected readonly router = inject(Router);
    private readonly inputService = inject(InputService);
    private readonly promptService = inject(PromptService); // Needed for hillclimbing only
    private readonly currentUser = inject(AuthenticationService).currentUser;
    private readonly document = inject(DOCUMENT);

    protected readonly SIMULATABLE_OBJECTS = SIMULATABLE_OBJECTS;
    protected readonly OBJECTS = OBJECTS;
    protected readonly OBJECT_DISPLAY_NAME = OBJECT_DISPLAY_NAMES;
    protected readonly SKELETON_TYPES = SKELETON_TYPES;
    protected readonly JOB_STEP_STATUS = JOB_STEP_STATUS;
    protected readonly MODEL_INPUT_TYPE = MODEL_INPUT_TYPE;
    protected readonly GGX_GLOBAL_OBJECTS = GGX_GLOBAL_OBJECTS;
    protected readonly SCHEDULED_JOB_STATUS = SCHEDULED_JOB_STATUS;
    protected readonly TAB_ROUTES = TAB_ROUTES;
    protected readonly RouterHelper = RouterHelper;
    protected readonly MAX_ERROR_LOG_LENGTH = 5 * 1024 * 1024;
    protected readonly CONVERSATION_COLUMN = CONVERSATION_COLUMN;
    protected readonly DataGridHelpers = DataGridHelpers;
    protected readonly previewDataRowPanel = {
        visible: signal(false),
        title: signal<string | null>('Data Preview'), // NOTE: Kept a variable in case we want to show report name later
        dataGridInstance: signal<DxDataGridComponent>(null),
        rowIndex: signal<number | null>(null),
        view: signal<'chat' | 'data'>('data'),
        visibleRowIndex: computed((): number =>
            DataGridHelpers.getVisibleRowIndex(
                this.previewDataRowPanel.dataGridInstance()?.instance,
                this.previewDataRowPanel.rowIndex(),
            ),
        ),
        showPrevious: computed((): boolean =>
            DataGridHelpers.showRowPopupPrevious(
                this.previewDataRowPanel.dataGridInstance()?.instance,
                this.previewDataRowPanel.rowIndex(),
            ),
        ),
        showNext: computed((): boolean =>
            DataGridHelpers.showRowPopupNext(
                this.previewDataRowPanel.dataGridInstance()?.instance,
                this.previewDataRowPanel.rowIndex(),
            ),
        ),
        content: computed(
            (): {
                name: string;
                value: string | number;
            }[] => {
                this.previewDataRowPanel.visible();
                return DataGridHelpers.getRowPopupContent(
                    this.previewDataRowPanel.dataGridInstance()?.instance,
                    this.previewDataRowPanel.rowIndex(),
                );
            },
        ),
        chatMessages: computed(
            (): {
                errorMessage?: string;
                interactions?: InteractionAdapter[];
                rawMessage: string;
            } | null => {
                const conversation = this.previewDataRowPanel
                    .content()
                    ?.find(field => field.name === CONVERSATION_COLUMN)?.value;

                return conversation ? parseChatMessage(String(conversation)) : null;
            },
        ),
        openPanel: (
            dataGrid: DxDataGridComponent,
            rowIndex: number | null,
            view: 'chat' | 'data' = 'data',
        ) => {
            this.previewDataRowPanel.dataGridInstance.set(dataGrid);
            this.previewDataRowPanel.rowIndex.set(rowIndex);
            this.previewDataRowPanel.view.set(view);
            this.previewDataRowPanel.visible.set(true);
        },
        onHide: () => {
            this.previewDataRowPanel.visible.set(false);
            this.previewDataRowPanel.view.set('data');
            this.previewDataRowPanel.rowIndex.set(null);
            this.previewDataRowPanel.dataGridInstance.set(null);
        },
    };

    /**
     * Form control for capturing the job name.
     * Initialized with an empty string (Marked as `required`).
     */
    protected readonly jobNameFormControl = new FormControl('', Validators.required);

    // If the component was loaded by the router - then it is a job page -- i.e. isJobDetailsPage = true
    // If it was loaded as the Objects > Dashboard tab - i.e. isJobDetailsPage = false
    protected readonly isJobDetailsPage = toSignal(
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this.route.snapshot.routeConfig?.path !== TAB_ROUTES.DASHBOARD.toString()),
        ),
        { initialValue: this.route.snapshot.routeConfig?.path !== TAB_ROUTES.DASHBOARD.toString() },
    );

    protected readonly writeAccess = computed(() =>
        this.permissionService.canAccessObject(
            Helpers.getPermissionName(this.object(), ACTIONS.WRITE),
        ),
    );

    protected readonly rerunLink = computed(() => {
        const currentId = this.job()?.inputsSectionData?.currentObjectData.entityId;

        return {
            url: `${RouterHelper.buildRoute({
                object: this.object(),
                id: currentId,
                projectSlug: this.item()?.projectSlug,
            })}/run-job`,
            queryParams: {
                type: this.job()?.executionType,
                entityId: currentId,
                jobId: this.job()?.id,
                object: this.object(),
            },
        };
    });

    protected readonly rerunReportsLink = computed(() => {
        const rerunLink = this.rerunLink();
        return {
            ...rerunLink,
            queryParams: { ...rerunLink.queryParams, rerunReports: true },
        };
    });

    /**
     * Link to run the job using Hill Climbing execution type.
     * When making changes to this URL - also need to change job-history > onHillClimbOnPrompt()
     */
    protected readonly hillClimbingLink = computed(() => {
        const currentId = this.job().inputsSectionData.currentObjectData.entityId;

        return {
            url: `${RouterHelper.buildRoute({
                object: this.object(),
                id: currentId,
                projectSlug: this.item()?.projectSlug,
            })}/run-job`,
            queryParams: {
                type: EXECUTION_TYPE.HILL_CLIMBING,
                entityId: currentId,
                object: this.object(),
                jobId: this.job().id, // Fetch evaluation logic and reports used from here
                baseHillclimbSimId: this.job().id,
            },
        };
    });

    protected readonly isJobDeletable = computed(() => {
        if (this.job().isVerified) return false;

        // FIXME: This check doesn't seem right. What do we want here?
        return this.permissionService.canAccessObject(
            Helpers.getPermissionName(this.object(), ACTIONS.WRITE),
        );
    });

    protected readonly isJobPinnable = computed(() =>
        this.permissionService.canAccessObject(
            Helpers.getPermissionName(this.object(), ACTIONS.WRITE),
        ),
    );

    protected readonly isRerunAllFiguresOptionVisible = computed(() =>
        Object.fromEntries(
            this.evalTabs().map(tab => {
                return [
                    tab.title,
                    [JOB_STEP_STATUS.SUCCESSFUL, JOB_STEP_STATUS.PARTIAL_SUCCESS].includes(
                        this.job().jobSteps.find(step => step.reportName === tab.title)?.status,
                    ) &&
                        !this.comparison.isComparisonViewActive() &&
                        !this.job().isVerified &&
                        this.job().createdBy === this.currentUser()?.name,
                ];
            }),
        ),
    );

    protected readonly jobAssets = computed(
        () =>
            this.getObjectAssets(
                uniqWith(
                    // NOTE: Ideally only txnId or definition (for hillclimb) should be present for every
                    //       objectId/objectType/proxyType combination
                    (this.job()?.dataLogics || []).flatMap(dataLogic => dataLogic.jobAssets || []),
                    (x, y) =>
                        x.entityId === y.entityId &&
                        x.objectType === y.objectType &&
                        x.proxyType === y.proxyType,
                ),
            ).jobAssets,
    );

    private getRunDurationBreakdown(reportName: string): RunDurationBreakdown {
        const steps = this.job()?.jobSteps || [];
        const dataStep = steps.find(step =>
            step.title.startsWith(SIMULTATION_TIME.DATA_CREATION.valueOf()),
        );
        const execStep = steps.find(step =>
            step.title.startsWith(SIMULTATION_TIME.EXECUTION.valueOf()),
        );
        const reportStep = steps.find(
            step =>
                step.title.startsWith(SIMULTATION_TIME.REPORT.valueOf()) &&
                step.reportName === reportName,
        );
        const workflowSteps = [dataStep, execStep, reportStep].filter(step => !!step);
        const totalDuration = sumBy(workflowSteps, x => x.duration || 0);

        return {
            totalDuration,
            lifecycle: [
                {
                    label: 'Created At',
                    value: this.job()?.createdDate,
                    author: this.job()?.createdBy,
                    markerClass: 'tw:border-surface-400',
                },
                {
                    label: 'Submitted At',
                    value: this.job()?.createdDate,
                    markerClass: 'tw:border-blue-500',
                },
                {
                    label: 'Started At',
                    value: workflowSteps[0]?.startTime,
                    markerClass: 'tw:border-blue-500',
                },
                {
                    label: 'Completed At',
                    value: [...workflowSteps].reverse().find(x => x.endTime)?.endTime,
                    markerClass: 'tw:border-green-500',
                },
            ],
            stages: workflowSteps.map(step => {
                const share = totalDuration ? ((step.duration || 0) / totalDuration) * 100 : 0;

                return {
                    label: step.label,
                    status: step.status,
                    durationLabel: TimeTakenPipe.secondsToHms(step.duration),
                    shareLabel: `${Math.round(share)}%`,
                    shareValue: share,
                };
            }),
        };
    }

    /**
     * Fetched the lineage data to show assets for a report tab.
     */
    private getObjectAssets(assets: JobAsset[] = []): {
        jobAssets: JobAssetWithLink[];
        jobAssetGroups: Record<string, JobAssetGroup[]>;
    } {
        const objectTypeOrder: { [key in OBJECTS]?: number } = Object.fromEntries(
            [
                OBJECTS.REPORT,
                OBJECTS.PIPELINE,
                OBJECTS.RAG,
                OBJECTS.PROMPT,
                OBJECTS.FOUNDATION_MODEL,
                OBJECTS.GLOBAL_FUNCTION,
            ].map((x, i) => [x, i]),
        );
        const jobAssets = assets
            // Monitoring is just a informational object, it does not have any logic. So, remove it
            .filter(asset => asset.objectType !== OBJECTS.MONITORING)
            .map(asset => {
                const link = RouterHelper.buildRoute({
                    module: OBJECT_MODULE_MAP[asset.objectType],
                    object: asset.objectType,
                    id: asset.entityId,
                    // FIXME: We allow reports from other projects to be used. So this should techically come from
                    //       job asset data instead of the simulation entity
                    projectSlug: this.item()?.projectSlug,
                });

                return {
                    ...asset,
                    link: `${link}/preview/${asset.entityTransactionId}`,
                    bgColor: OBJECT_INFO[asset.objectType]?.background,
                };
            });

        const jobAssetGroups = mapValues(groupBy(jobAssets, 'proxyType'), group =>
            sortBy(
                group.map(item => ({
                    name: item.entityName,
                    lastModifiedDate: item.entityTransactionIssuedAt,
                    objectType: item.objectType,
                    url: item.link,
                    bgColor: item.bgColor,
                    isModified: !!item.promptTemplate,
                })),
                [x => objectTypeOrder[x.objectType] || 99, 'name'],
            ),
        );
        if (this.object() === OBJECTS.REPORT) {
            // For report we only show the dashboard as it also contains the report.
            return {
                jobAssets,
                jobAssetGroups:
                    jobAssetGroups.Dashboard?.length > 0
                        ? { Dashboard: jobAssetGroups.Dashboard }
                        : {},
            };
        }

        return { jobAssets, jobAssetGroups };
    }

    protected readonly jobStepLogs = toSignal(
        toObservable(this.job).pipe(
            map(job => job?.jobSteps || []),
            distinctUntilChanged<JobStep[]>(isEqual),
            mergeMap(steps => {
                return forkJoin(
                    steps?.map(step => {
                        return from(
                            this.object() !== OBJECTS.ANNOTATION_QUEUE
                                ? this.simulationService.getLogsForStep(
                                      this.job().id,
                                      step.id,
                                      // Read an additional 3 bytes - for us to know if there is more remaining logs
                                      // we are not showing these will be truncated to ...
                                      this.MAX_ERROR_LOG_LENGTH + 3,
                                  )
                                : this.annotationQueueService.getLogsForStep(
                                      this.job().id,
                                      step.id,
                                      // Read an additional 3 bytes - for us to know if there is more remaining logs
                                      // we are not showing these will be truncated to ...
                                      this.MAX_ERROR_LOG_LENGTH + 3,
                                  ),
                        ).pipe(
                            map(logs => ({ stepId: step.id, reportName: step.reportName, logs })),
                        );
                    }),
                );
            }),
            map(responses =>
                responses.map(response =>
                    response.logs
                        ? {
                              ...response,
                              logs: TruncatePipe.truncate(response.logs, this.MAX_ERROR_LOG_LENGTH),
                          }
                        : response,
                ),
            ),
        ),
        { initialValue: [] },
    );

    protected readonly reportLink = computed(() =>
        keyBy(
            this.evalTabs().map(tab => ({
                reportId: tab.reportId,
                link: this.jobAssets().find(
                    asset => asset.objectType === OBJECTS.REPORT && asset.entityId === tab.reportId,
                )?.link,
            })),
            'reportId',
        ),
    );

    protected readonly canEditEvals = computed(
        () =>
            !!this.job()?.id &&
            !this.comparison.isComparisonViewActive() &&
            !this.job().isVerified &&
            this.job().createdBy === this.currentUser()?.name,
    );

    constructor() {
        combineLatest({
            comparisonId: toObservable(this.comparisonId),
            tab: toObservable(this.activeTabParam),
            showLogs: toObservable(this.showLogsParam),
        })
            .pipe(auditTime(0), takeUntilDestroyed())
            .subscribe(() => this.loadFromQueryParams());

        combineLatest({
            activeIndex: toObservable(this.activeIndex),
            evalTabs: toObservable(this.evalTabs),
        })
            .pipe(auditTime(0), takeUntilDestroyed())
            .subscribe(() => {
                const activeEvalTab = this.evalTabs()[this.activeIndex() - 2];
                // Load Eval Data in case of previewData.isVisible is active by default
                // previewData.isvisible will be active by default in case evaluation with no report
                if (activeEvalTab?.previewData?.isVisible()) {
                    this.loadEvalData(activeEvalTab);
                }
            });

        combineLatest({
            object: toObservable(this.object),
            objectId: toObservable(computed(() => this.id() || this.routeObjectId())),
            simulationId: toObservable(this.routeSimulationId),
            isJobDetailsPage: toObservable(this.isJobDetailsPage),
        })
            .pipe(auditTime(0), distinctUntilChanged(isEqual), takeUntilDestroyed())
            .subscribe(params => this.fetchDashboardDetails(params));
    }

    private fetchDashboardDetails(params: {
        object: OBJECTS | null;
        objectId: number | null;
        simulationId: number | null;
        isJobDetailsPage: boolean;
    }) {
        const { object, objectId, simulationId, isJobDetailsPage } = params;
        if (!object || !objectId) return;

        // If jobId not there and isJobDetailsPage is false → go to 404
        const jobId = simulationId || null;
        // If jobId not there and isJobDetailsPage is false → go to 404
        if (!jobId && isJobDetailsPage) {
            this.resetRouteScopedState();
            this.notFound.set(true);
            return;
        }

        this.resetRouteScopedState();
        this.service = this.injector.get<any>(OBJECT_SERVICE_MAPPER[object]);
        this.isItemLoading.set(true);
        this.forbidden.set(false);
        this.service
            .getById(objectId, {
                auth: true,
            })
            .pipe(
                catchError(error => {
                    if (
                        error instanceof HttpErrorResponse &&
                        error.status === Number(HttpStatusCode.Forbidden)
                    ) {
                        this.forbidden.set(true);
                        return EMPTY;
                    }
                    return throwError(() => error);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ result }) => {
                this.item.set(result);
                this.forbidden.set(!this.item()?.actions?.[ACTIONS.VISIBLE]);
                if (isNil(this.currentProject()) && this.item()?.projectSlug) {
                    RouterHelper.switchToObjectProject(
                        this.router,
                        this.currentProject(),
                        this.item()?.projectSlug,
                    );
                }
                if (this.id() || this.routeObjectId()) {
                    this.loadReports(jobId);
                    if (GGX_GLOBAL_OBJECTS.includes(object) && jobId) {
                        this.reportEvaluations.isLoading.set(true);
                        this.inputService
                            .getInputs({
                                case: INPUT_CASES.DATA_LOGIC_INPUTS,
                                projectSlug: this.currentProject(),
                                simulationId: jobId,
                                entityType: object,
                            })
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe(dataLogicInputs => {
                                this.reportEvaluations.dataLogicInputList.set(
                                    dataLogicInputs.result,
                                );
                            })
                            .add(() => this.reportEvaluations.isLoading.set(false));
                    }
                }

                if (!SIMULATABLE_OBJECTS.includes(object)) {
                    this.loadReportsForNonSimulatableObject(objectId);
                }
            })
            .add(() => this.isItemLoading.set(false));
    }

    private resetRouteScopedState() {
        this.item.set(null);
        this.job.set(null);
        this.evalTabs.set([]);
        this.notFound.set(false);
        this.forbidden.set(false);
        this.jobNameFormControl.reset('');

        this.dashboardTab.defaultDashboardJobId.set(null);
        this.dashboardTab.jobList.set([]);
        this.dashboardTab.selectedJobId.set(null);
        this.dashboardTab.showChangeOption.set(false);

        this.comparison.flipped.set(false);
        this.comparison.isComparisonViewActive.set(false);
        this.comparison.isLoading.set(false);
        this.comparison.isBaseHillclimbJobDeleted.set(false);
        this.comparison.leftJob.set(null);
        this.comparison.objectList.set([]);
        this.comparison.rightJob.set(null);
        this.comparison.selectedJobId.set(null);
        this.comparison.selectedObjectId.set(null);
    }

    /**
     * Load the comparison job based on the queryparam
     */
    private loadFromQueryParams() {
        if (this.comparisonId() && this.comparisonId() !== this.comparison.rightJob()?.id) {
            this.comparison.selectedJobId.set(this.comparisonId());
            if (this.job() && this.comparison.isComparisonViewActive()) {
                this.loadComparisonReports();
            }
        }

        if (
            !isNil(this.activeTabParam()) &&
            this.activeTabParam() >= 0 &&
            this.activeTabParam() !== this.activeIndex()
        ) {
            this.activeIndex.set(this.activeTabParam());
        }
    }

    ngAfterViewChecked() {
        this.cdr.detectChanges();
    }

    protected loadEvalData(reportTab: ReportTab) {
        // FIXME: For comparison - we keep previewData as null. We need to handle comparison better
        if (!reportTab.previewData) return;

        if (reportTab.previewData.columns().length > 0) return; // Data was already loaded earlier

        reportTab.previewData.isLoading.set(true);
        this.simulationService
            .getEvalResultPreview(reportTab.evaluationId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => {
                // FIXME: Error case has not been handled right

                reportTab.previewData.columns.set(
                    result.data.columns.map(col => ({
                        dataField: col,
                        caption: col,
                        allowEditing: false,
                        cssClass: 'tw:cursor-pointer tw:max-w-[20rem]',
                        cellTemplate:
                            col === CONVERSATION_COLUMN ? 'chatMessagesTemplate' : undefined,
                    })),
                );
                reportTab.previewData.gridDatasource.set(
                    new ArrayStore({
                        data: DataGridHelpers.transformToDevExData(result.data),
                    }),
                );

                if (result.truncated) {
                    reportTab.previewData.infoTextMarker.set(
                        `Results truncated to ${result.total} records. Showing page {0} of {1} ({2} items)`,
                    );
                } else {
                    reportTab.previewData.infoTextMarker.set('Showing page {0} of {1} ({2} items)');
                }
            })
            .add(() => reportTab.previewData.isLoading.set(false));
    }

    protected toggleEvalDataPreview(reportTab: ReportTab) {
        if (!reportTab.previewData) return;

        reportTab.previewData.isVisible.update(x => !x);

        if (reportTab.previewData.isVisible()) {
            this.loadEvalData(reportTab);
        }
    }

    protected onPreviewDataRowClicked(
        event: DxDataGrid.RowClickEvent,
        dataGrid: DxDataGridComponent,
    ) {
        if (event.rowType !== 'data') return;
        this.previewDataRowPanel.openPanel(dataGrid, event.data?._index ?? null);
    }

    protected deleteJob(event: MouseEvent) {
        this.confirmationService.confirm({
            key: 'confirm-popup',
            target: (event.target as any).parentElement,
            message: 'Are you sure you want to delete this job?',
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            rejectButtonProps: {
                severity: 'secondary',
                outlined: true,
                size: 'small',
                styleClass: 'rounded',
            },
            acceptButtonProps: {
                severity: 'danger',
                size: 'small',
                styleClass: 'rounded',
            },
            accept: () => {
                this.simulationService
                    .deleteSimulation(this.job().id)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.toast.showSuccess('Job deleted successfully');
                        this.redirectToJobHistory();
                    });
            },
        });
    }

    private redirectToJobHistory() {
        const url = RouterHelper.buildRoute({
            object: this.object(), // always required
            id: this.id(), // always required
            projectSlug: this.item()?.projectSlug,
        });

        this.router.navigateByUrl(`${url}/${TAB_ROUTES.JOBS}`);
    }

    protected loadReports(simulationId: number) {
        this.isJobLoading.set(true);
        this.notFound.set(false);
        this.forbidden.set(false);

        // When the job ID exists - fetch the job details otherwise fetch the lastest job(for dashboard tab)
        (simulationId
            ? this.simulationService.getSimulation(simulationId).pipe(
                  catchError(err => {
                      if (
                          err instanceof HttpErrorResponse &&
                          err.status === Number(HttpStatusCode.NotFound)
                      ) {
                          this.notFound.set(true);
                      } else if (
                          err instanceof HttpErrorResponse &&
                          err.status === Number(HttpStatusCode.Forbidden)
                      ) {
                          this.forbidden.set(true);
                      }
                      return throwError(() => err);
                  }),
              )
            : this.service.getLatestSimulation(this.id())
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(jobResponse => {
                this.job.set(JobModel.createFromAPI(jobResponse.result));
                if (
                    (this.job().baseHillclimbSimId &&
                        this.job().jobStatus === SCHEDULED_JOB_STATUS.COMPLETED) ||
                    this.comparison.selectedJobId()
                ) {
                    this.showComparisonOption();
                }

                // Process dashboard report tabs
                this.updateJobReports();
                this.checkAccessAndGrantEditPermission();
            })
            .add(() => this.isJobLoading.set(false));
    }

    protected addEvaluation(): void {
        if (this.addEvaluationDialog.isSubmitting()) return;

        const name = this.addEvaluationDialog.nameControl.value?.trim();
        const dataLogicSourceId = this.addEvaluationDialog.dataLogicSourceControl.value;
        if (!name || !dataLogicSourceId) {
            this.addEvaluationDialog.nameControl.markAsTouched();
            this.addEvaluationDialog.dataLogicSourceControl.markAsTouched();
            return;
        }

        this.addEvaluationDialog.isSubmitting.set(true);
        this.simulationService
            .addInlineDataLogic(this.job().id, {
                name,
                dataLogicSourceId,
                sortOrder: this.job().dataLogics.length + 1,
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => this.simulationService.getSimulation(this.job().id)),
            )
            .subscribe({
                next: ({ result }) => {
                    this.updateJobData(JobModel.createFromAPI(result));
                    // Update the query param to show the newly added evaluation tab
                    this.updateTabQueryParam(this.evalTabs().length + 2 - 1);
                    this.addEvaluationDialog.close();
                },
            })
            .add(() => this.addEvaluationDialog.isSubmitting.set(false));
    }

    protected deleteEvaluation(btnTarget: Button, reportTab: ReportTab): void {
        const evaluationId = reportTab.evaluationId;
        if (!evaluationId) return;

        this.confirmationService.confirm({
            key: 'confirm-popup',
            target: btnTarget.el.nativeElement,
            message: 'Are you sure you want to delete this evaluation?',
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            rejectButtonProps: {
                severity: 'secondary',
                outlined: true,
                size: 'small',
                styleClass: 'rounded',
            },
            acceptButtonProps: {
                severity: 'danger',
                size: 'small',
                styleClass: 'rounded',
            },
            accept: () => {
                const tabIndex = this.evalTabs().findIndex(
                    tab => tab.evaluationId === evaluationId,
                );
                this.simulationService
                    .deleteInlineDataLogic(this.job().id, evaluationId)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.updateJobData({
                            ...this.job(),
                            dataLogics: this.job().dataLogics.filter(
                                dataLogic => dataLogic.id !== evaluationId,
                            ),
                        });
                        this.updateTabQueryParam(
                            this.evalTabs().length > 0
                                ? Math.min(2 + tabIndex, this.evalTabs().length + 1)
                                : 1,
                        );
                        this.toast.showSuccess('Evaluation deleted successfully');
                    });
            },
        });
    }

    /**
     * Initializes jobName formControl.
     * Checks if the current user has permission to edit the job via `permissionService`;
     * if so then grant editing access.
     */
    private checkAccessAndGrantEditPermission() {
        if (
            this.job().createdBy === this.currentUser().name ||
            this.permissionService.canAccessObject(
                Helpers.getPermissionName(this.object(), ACTIONS.WRITE),
            )
        ) {
            this.jobNameFormControl.setValue(this.job().descriptionSectionData.name);
            this.jobNameFormControl.enable();
        }
    }

    /**
     * Handles updates to the job name field.
     * Checks if the new name value is different from the current job name.
     * If the value has not changed, the avoids unnecessary API calls.
     *
     * @param value The new job name entered by the user.
     */
    protected onNameUpdate(value: string) {
        if (value === this.job().name) return;

        this.simulationService
            .updateSimulation(this.job().id, {
                name: value,
                description: this.job().descriptionSectionData.description,
                submissionDate: this.job().descriptionSectionData.submissionDate
                    ? moment(this.job().descriptionSectionData.submissionDate).format(
                          MOMENT_UTC_DATETIME_FORMAT,
                      )
                    : null,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: response => {
                    this.job.set(JobModel.createFromAPI(response.result));
                    this.toast.showSuccess(`${'Name is updated successfully'}`);
                },
            });
    }

    protected refreshJob() {
        this.isRefreshing.set(true);
        this.simulationService
            .getSimulation(this.job().id)
            .subscribe(jobResponse => {
                this.job.set(JobModel.createFromAPI(jobResponse.result));
                this.updateJobReports(false);
                // If job is failed navigate to job result page
                if (this.job().jobStatus === SCHEDULED_JOB_STATUS.FAILED) {
                    this.updateTabQueryParam(1);
                }
                if (
                    this.job().baseHillclimbSimId &&
                    this.job().jobStatus === SCHEDULED_JOB_STATUS.COMPLETED
                ) {
                    this.showComparisonOption();
                }
            })
            .add(() => this.isRefreshing.set(false));
    }

    protected updateJobReports(changeTab = true) {
        if (this.job()?.id) {
            // Get UI Configured Reports from figures having reportOutputId,
            // other Config related reports like tracking will not have reportOutputId and needs to
            // be handled separately
            const evalTabs = (this.job().dataLogics || []).map(dataLogic => {
                const tabReports = dataLogic.figures.map(leftSimFigure => ({
                    reportOutputId: leftSimFigure.reportOutputId,
                    width: leftSimFigure.width,
                    leftSimFigure,
                }));

                return {
                    title: dataLogic.name,
                    reportId: dataLogic.reportVersionId,
                    evaluationId: dataLogic.id,
                    data: tabReports,
                    disabled: false,
                    previewData: {
                        isVisible: signal(!dataLogic.reportVersionId),
                        isLoading: signal(true),
                        gridDatasource: signal(
                            undefined,
                        ) as ReportTab['previewData']['gridDatasource'],
                        columns: signal([]) as ReportTab['previewData']['columns'],
                        infoTextMarker: signal(''),
                    },
                    // details visible be default for evaluation with no reports
                    isDetailsVisible: signal(!dataLogic.reportVersionId),
                    isAssetsVisible: signal(false),
                    isLogsVisible: signal(false),
                };
            });

            this.evalTabs.set(evalTabs);
        }

        if (changeTab) {
            // Select which tab to show by default
            // Tab0 -> Details, Tab1 -> Result, Tab2 -> Tracking or DashboardViews
            if (this.showLogsParam()) {
                this.showLogs();
            } else if (
                [SCHEDULED_JOB_STATUS.RUNNING, SCHEDULED_JOB_STATUS.COMPILING].includes(
                    this.job()?.jobStatus,
                )
            ) {
                this.showLogs();
            } else if (this.job()?.jobStatus === SCHEDULED_JOB_STATUS.COMPLETED) {
                if (this.activeIndex() < 2) {
                    this.updateTabQueryParam(this.evalTabs().length > 0 ? 2 : 1);
                }
            } else {
                // Scheduled, Compiling, Queued, Running, Failed
                this.updateTabQueryParam(1);
            }
        }
    }

    protected showLogs() {
        this.updateTabQueryParam(1);
        scrollToBottom();
        //Remove the showLogs query param from the url after scrolling
        setTimeout(() => {
            this.showLogsParam.set(null);
        }, 10);
    }

    protected updateJobData(job: JobModel) {
        this.job.set(job);
        // Process dashboard report tabs
        this.updateJobReports(false);

        if (
            (this.job().baseHillclimbSimId &&
                this.job().jobStatus === SCHEDULED_JOB_STATUS.COMPLETED) ||
            this.comparison.selectedJobId()
        ) {
            this.showComparisonOption();
        }
    }

    /**
     * Load the latest simulation for a non simulatable object.
     * @param id Id of the object
     */
    private loadReportsForNonSimulatableObject(id: number) {
        const service: ReportsService | GlobalFunctionService = this.injector.get<any>(
            OBJECT_SERVICE_MAPPER[this.object()],
        );

        this.isJobLoading.set(true);
        service
            .getLatestSimulation(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                if (response.result.id) {
                    this.job.set(JobModel.createFromAPI(response.result));
                }
            })
            .add(() => this.isJobLoading.set(false));
    }

    protected showDashboardChangeOption() {
        this.dashboardTab.showChangeOption.set(true);
        this.dashboardTab.selectedJobId.set(this.job().id);
        this.dashboardTab.defaultDashboardJobId.set(this.job().id);
        if (this.dashboardTab.jobList().length === 0) {
            this.dashboardTab.isJobListLoading.set(true);
            this.simulationService
                .getDataList({
                    entityId: this.id(),
                    statuses: [SCHEDULED_JOB_STATUS.COMPLETED],
                    simple: true,
                })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(response =>
                    this.dashboardTab.jobList.set(
                        response.result.sort(job => new Date(job.createdDate).getTime()),
                    ),
                )
                .add(() => this.dashboardTab.isJobListLoading.set(false));
        }
    }

    protected onDashboardChangeCancelClick() {
        this.dashboardTab.showChangeOption.set(false);
        if (this.job().id !== this.dashboardTab.defaultDashboardJobId()) {
            this.loadReports(this.dashboardTab.defaultDashboardJobId());
        }
    }

    protected onDashboardChangeSaveClick() {
        if (this.job().id !== this.dashboardTab.defaultDashboardJobId()) {
            this.dashboardTab.isSavingDashboard.set(true);
            this.service
                .setDefaultDashboard(this.id(), this.dashboardTab.selectedJobId())
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.toast.showSuccess('Dashboard is updated successfully');
                    this.dashboardTab.showChangeOption.set(false);
                })
                .add(() => this.dashboardTab.isSavingDashboard.set(false));
        } else {
            this.toast.showWarning(
                'No changes needed - Current job is already the default for dashboard',
            );
        }
    }

    /**
     * Opens the job in a new window.
     */
    protected openInNewWindow(): void {
        const url = RouterHelper.buildRoute({
            object: this.object(), // always required
            id: this.id(), // always required
            projectSlug: this.item()?.projectSlug,
        });

        this.window.openInNewTab(`${url}/${TAB_ROUTES.SIMULATION}/${this.job().id}`);
    }

    protected pinDashboard(): void {
        this.isJobPinLoading.set(true);
        this.simulationService
            .pinSimulation(this.job().id, { isPinned: !this.job().isPinned })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                if (response.result.isPinned) {
                    this.toast.showSuccess('Dashboard pinned successfully');
                } else {
                    this.toast.showSuccess('Dashboard unpinned successfully');
                }
                this.job.set(JobModel.createFromAPI(response.result));
                // Update the job name form control
                this.jobNameFormControl.setValue(this.job().name);
            })
            .add(() => this.isJobPinLoading.set(false));
    }

    protected showComparisonOption() {
        this.comparison.leftJob.set(this.job());
        this.comparison.isComparisonViewActive.set(true);
        // trigger a window resize  to get the state changes for plotly
        window.dispatchEvent(new Event('resize'));
        if (this.job().baseHillclimbSimId && !this.comparison.isBaseHillclimbJobDeleted()) {
            // set the base hillclimb simulation id for comparison if it exists
            this.comparison.selectedJobId.set(this.job().baseHillclimbSimId);
            this.updateComparisonQueryparam();
            this.comparison.flipped.set(true); // For hillclimbing we show flipped view for easier comparison
        }
        if (this.id()) {
            this.comparison.selectedObjectId.set(Number(this.id()));
        }
        if (this.comparison.objectList().length === 0) {
            this.comparison.isObjectListLoading.set(true);
            this.service
                .getAll()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(response => {
                    this.comparison.objectList.set(
                        response.result.sort(object => new Date(object.createdDate).getTime()),
                    );
                    this.getSimulationList();
                })
                .add(() => this.comparison.isObjectListLoading.set(false));
        }
        if (this.comparison.selectedJobId()) {
            this.loadComparisonReports(); // Load the reports for the selected job
        } else {
            // Need to reload the existing tabs to get the state changes for plotly
            setTimeout(() => this.comparison.isLoading.set(false), 1);
        }
    }

    protected toggleSignal(sig: WritableSignal<boolean>) {
        sig.update(x => !x);
    }

    protected getSimulationList() {
        this.comparison.isJobListLoading.set(true);
        if (this.comparison.rightJob()) {
            this.removeComparisonJob();
        }
        this.simulationService
            .getDataList({
                entityId: this.comparison.selectedObjectId(),
                statuses: [SCHEDULED_JOB_STATUS.COMPLETED],
                simple: true,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response =>
                this.comparison.jobList.set(
                    response.result.sort(job => new Date(job.createdDate).getTime()),
                ),
            )
            .add(() => this.comparison.isJobListLoading.set(false));
    }

    // Stops the comparison view
    protected stopComparisonOption() {
        this.removeComparisonJob();
        this.comparison.isComparisonViewActive.set(false);
    }

    // Removes the current job from comparison to restore the current job tabs correctly
    protected removeComparisonJob() {
        this.comparison.leftJob.set(null);
        this.comparison.rightJob.set(null);
        this.comparison.selectedJobId.set(null);
        this.comparison.flipped.set(false);
        this.updateComparisonQueryparam();

        if (this.job()?.jobStatus === SCHEDULED_JOB_STATUS.COMPLETED) {
            this.updateJobReports(true);
        } else {
            this.evalTabs.set([]); // Clear the report tabs
            this.updateTabQueryParam(1); // Set to Job Results tab
        }

        // trigger a window resize  to get the state changes for plotly
        window.dispatchEvent(new Event('resize'));
    }

    protected updateComparisonQueryparam() {
        this.comparisonId.set(this.comparison.selectedJobId());
    }

    /**
     * Updates the query parameters with tab and figure information
     */
    protected updateTabQueryParam(tabIndex: any) {
        this.activeTabParam.set(Number(tabIndex));
    }

    protected loadComparisonReports() {
        // If no comparison job is selected, remove any existing comparison and exit
        if (!this.comparison.selectedJobId()) {
            this.removeComparisonJob();
            return;
        }

        // Set loading state and store the current job as the left job
        this.comparison.isLoading.set(true);

        // Fetch the simulation data for the selected (right) job
        this.simulationService
            .getComparisonData({
                leftJobId: this.job().id,
                rightJobId: this.comparison.selectedJobId(),
                referenceJob:
                    this.job().executionType === EXECUTION_TYPE.HILL_CLIMBING ? 'left' : 'both',
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => {
                if (
                    isNil(result.rightJob) &&
                    this.job().executionType === EXECUTION_TYPE.HILL_CLIMBING
                ) {
                    this.toast.showWarning(
                        `Unable to fetch the Baseline for this Hillclimb, base job may have been deleted. Switching to default view`,
                    );
                    this.comparison.isBaseHillclimbJobDeleted.set(true);
                    this.stopComparisonOption();
                } else {
                    this.comparison.leftJob.set(JobModel.createFromAPI(result.leftJob));
                    this.comparison.rightJob.set(JobModel.createFromAPI(result.rightJob));
                    this.loadHillclimbPrompts();
                }
                this.evalTabs.set(result.evaluations);
                this.updateTabQueryParam(this.evalTabs().length > 0 ? 2 : 1);
            })
            .add(() => this.comparison.isLoading.set(false));
    }

    private loadHillclimbPrompts() {
        this.hillClimb.isLoading.set(true);
        const leftJob = this.comparison.leftJob();
        const rightJob = this.comparison.rightJob();

        if (!leftJob || !rightJob) return;

        // Map to hold merged prompts by entityId
        const mergedPromptsMap = new Map<
            number,
            {
                entityId: number;
                entityName: string;
                entityTransactionId?: number;
                leftPromptTemplate?: string;
                rightPromptTemplate?: string;
            }
        >();

        // Collect all modified prompt-template assets from both jobs
        // Each asset is tagged with a side ('left' or 'right') for merging later
        const promptAssets = [
            ...JobModel.getHillClimbPromptAssets(leftJob)
                .filter(
                    x =>
                        x.objectType === OBJECTS.PROMPT &&
                        x.proxyType === 'current' &&
                        !!x.promptTemplate,
                )
                .map(asset => ({ ...asset, side: 'left' })),
            ...JobModel.getHillClimbPromptAssets(rightJob)
                .filter(
                    x =>
                        x.objectType === OBJECTS.PROMPT &&
                        x.proxyType === 'current' &&
                        !!x.promptTemplate,
                )
                .map(asset => ({ ...asset, side: 'right' })),
        ];

        // This merges left and right prompt templates under the same entityId
        promptAssets.forEach(asset => {
            if (!mergedPromptsMap.get(asset.entityId)) {
                mergedPromptsMap.set(asset.entityId, {
                    entityId: asset.entityId,
                    entityName: asset.entityName,
                    entityTransactionId: asset.entityTransactionId,
                    leftPromptTemplate: asset.side === 'left' ? asset.promptTemplate : null,
                    rightPromptTemplate: asset.side === 'right' ? asset.promptTemplate : null,
                });
            } else {
                // Entry exists, update missing definition on the appropriate side
                if (
                    asset.side === 'left' &&
                    !mergedPromptsMap.get(asset.entityId).leftPromptTemplate
                ) {
                    mergedPromptsMap.get(asset.entityId).leftPromptTemplate = asset.promptTemplate;
                }
                if (
                    asset.side === 'right' &&
                    !mergedPromptsMap.get(asset.entityId).rightPromptTemplate
                ) {
                    mergedPromptsMap.get(asset.entityId).rightPromptTemplate = asset.promptTemplate;
                }
            }
        });

        // Identify prompts missing definitions on either side and fetch their snapshots
        // We use entityTransactionId to fetch the exact snapshot used in the job
        forkJoin(
            Array.from(mergedPromptsMap.values())
                .filter(x => !x.leftPromptTemplate || !x.rightPromptTemplate)
                .map(x => {
                    return this.promptService
                        .getById(x.entityId, { transactionId: x.entityTransactionId })
                        .pipe(
                            takeUntilDestroyed(this.destroyRef),
                            map(resp => ({
                                ...resp.result,
                                entityId: x.entityId,
                                entityName: x.entityName,
                            })),
                        );
                }),
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(prompts => {
                // Update merged prompts with fetched templates where they were missing
                prompts.forEach(prompt => {
                    const promptEntry = mergedPromptsMap.get(prompt.entityId);

                    if (!promptEntry.leftPromptTemplate) {
                        promptEntry.leftPromptTemplate = prompt.promptTemplate;
                    }

                    if (!promptEntry.rightPromptTemplate) {
                        promptEntry.rightPromptTemplate = prompt.promptTemplate;
                    }
                });
            })
            .add(() => {
                this.hillClimb.prompts.set(Array.from(mergedPromptsMap.values()));
                this.hillClimb.isLoading.set(false);
            });
    }

    private updateReportFigure(
        reportId: number,
        simFigureId: number,
        status: string,
        data?: SimulationFigure,
        rerunError: string | null = null,
    ) {
        this.evalTabs.update(tabs =>
            tabs.map(tab =>
                tab.reportId !== reportId
                    ? tab
                    : {
                          ...tab,
                          data: tab.data.map(item =>
                              item.leftSimFigure.id !== simFigureId
                                  ? item
                                  : {
                                        ...item,
                                        rerunError,
                                        leftSimFigure: { ...(data || item.leftSimFigure), status },
                                    },
                          ),
                      },
            ),
        );
    }

    protected rerunAllFigures(tab: ReportTab) {
        this.isRerunningAllFigures.set(true);
        // Set all figures to IN_PROGRESS
        tab.data.forEach(item => {
            this.updateReportFigure(tab.reportId, item.leftSimFigure.id, 'IN_PROGRESS');
        });

        forkJoin(
            tab.data.map(item =>
                this.simulationService
                    .rerunFigure(this.job().id, item.leftSimFigure.id, false)
                    .pipe(
                        tap({
                            next: ({ result }) =>
                                this.updateReportFigure(
                                    tab.reportId,
                                    item.leftSimFigure.id,
                                    'COMPLETED',
                                    result,
                                ),
                        }),
                        catchError((error: HttpErrorResponse) => {
                            this.updateReportFigure(
                                tab.reportId,
                                item.leftSimFigure.id,
                                'COMPLETED',
                                null,
                                Helpers.getErrorMessageString(error),
                            );
                            // Continue with other rerun figures
                            return of(null);
                        }),
                    ),
            ),
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe()
            .add(() => this.isRerunningAllFigures.set(false));
    }

    protected rerunReportFigure(reportId: number, simFigureId: number) {
        // Track this figure as being rerun to preserve IN_PROGRESS status
        this.updateReportFigure(reportId, simFigureId, 'IN_PROGRESS');
        this.simulationService
            .rerunFigure(this.job().id, simFigureId, false)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ result }) =>
                    this.updateReportFigure(reportId, simFigureId, 'COMPLETED', result),
                error: (error: HttpErrorResponse) =>
                    this.updateReportFigure(
                        reportId,
                        simFigureId,
                        'COMPLETED',
                        null,
                        Helpers.getErrorMessageString(error),
                    ),
            });
    }

    protected onFigureBlockClick(figure: SimulationFigure) {
        // If completed then scroll to that figure
        this.document.getElementById(`figure-${figure.id}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }
}
